import { themeAtom } from "../../shared/atoms";
import Datepicker from "../../shared/components/Datepicker/Datepicker";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { api, apiUrl } from "../../shared/services/service";
import { Bq9EngineRoomM, initalBq9EngineRoom, mapSqlToBq9EngineRoomForm } from "../models/bq9-engine-room.model";
import { BqCommVis } from "../models/bq-forms.models";
import { bqF9EngineRoomAtom } from "../state/baltic-form.atoms";
import { InputCell, formDate } from "../services/baltic-forms.utils";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";

interface Props {
  isCompletedForm: boolean;
  cacheInitialForm: (form: any) => void;
  readonly: boolean;
}

export const Bq9EngineRoom: React.FC<Props> = ({ readonly, isCompletedForm, cacheInitialForm }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(bqF9EngineRoomAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<Bq9EngineRoomM>(initalBq9EngineRoom);
  const [commentsVisible, setCommentsVisible] = useState<BqCommVis>({});

  function toggleComments(inputId: string) {
    const newComms = { ...commentsVisible };
    newComms[inputId] = !commentsVisible[inputId];

    setCommentsVisible(newComms);
  }

  function isCommentsVisible(inputId: string): boolean {
    return commentsVisible[inputId];
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checkedValue = (e.target as HTMLInputElement).value;

      setForm((prev) => {
        const currentList = [...prev.noxReductionTech];

        if (currentList.includes(checkedValue)) {
          return { ...prev, noxReductionTech: currentList.filter((v) => v !== checkedValue) };
        } else {
          return { ...prev, noxReductionTech: [...currentList, checkedValue] };
        }
      });
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  const updateDate = (date: Date | null, field: string) => {
    setForm((prevState) => {
      const _form = {
        ...prevState,
        [field]: date !== null ? date.toISOString() : null,
      };

      return _form;
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const isChecked = (val: string) => form.noxReductionTech?.includes(val);

  // Get
  useEffect(() => {
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      fetchData();
    } else {
      if (storageisEmpty) {
        fetchData();
      } else {
        setForm(_form);
        cacheInitialForm(_form);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm]);

  // Storage
  useEffect(() => {
    if (!isLoading) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const result = await api.get(`${apiUrl}/bq_EngineRoom`, {
        params: {
          imo_no: imoNumber,
          vessel_name: vesselName,
        },
      });
      const _formSql = result.data[0];
      const _form = mapSqlToBq9EngineRoomForm(_formSql);
      setForm(_form);
      setStoredForm(_form);
      cacheInitialForm(_form);
    } catch (error: unknown) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorSize="lg" loadingIndicatorClassName="text-green">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.inputsFocusBorder,
          theme.balticQuestionnaire.inputsFocusRing,
          theme.balticQuestionnaire.selectsFocusBorder,
          theme.balticQuestionnaire.selectsFocusRing,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.selectsBorder,
          theme.balticQuestionnaire.textsBg,
          theme.balticQuestionnaire.textsText,
          theme.balticQuestionnaire.textsBorder,
          theme.balticQuestionnaire.selectsScroller,
          theme.balticQuestionnaire.textAreasFocusBorder,
          theme.balticQuestionnaire.textAreasFocusRing,
        )}
      >
        <h1 className="custom-heading mb-4">Engine Room, Speed and Consumption</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Vessel is fitted with shaft generator:</label>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselHasShaftGenerator"
                    value="yes"
                    checked={form.vesselHasShaftGenerator === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselHasShaftGenerator"
                    value="no"
                    checked={form.vesselHasShaftGenerator === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
              </div>
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("vesselHasShaftGeneratorComments")}
                >
                  Comments
                </button>
              </label>
              {(form.vesselHasShaftGeneratorComments || isCommentsVisible("vesselHasShaftGeneratorComments")) && (
                <textarea
                  disabled={readonly}
                  name="vesselHasShaftGeneratorComments"
                  value={form.vesselHasShaftGeneratorComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
          </div>

          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Engine</h2>

          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Make and model of propulsion engine</label>
              <input
                disabled={readonly}
                type="text"
                name="propulsionEngineMakeModel"
                value={form.propulsionEngineMakeModel}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">What is the Propulsion Type</label>
              <input
                disabled={readonly}
                type="text"
                name="propulsionEngineType"
                value={form.propulsionEngineType}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-bold">MCR of main engine</label>
              <InputCell
                disabled={readonly}
                name="mainEngineMcrBhp"
                value={form.mainEngineMcrBhp}
                onChange={handleChange}
                trailingText="bhp"
                inputType="number"
              />
            </div>

            <div>
              <label className="block text-sm font-bold">MCR of main engine</label>
              <InputCell
                disabled={readonly}
                name="mainEngineMcrRpm"
                value={form.mainEngineMcrRpm}
                onChange={handleChange}
                trailingText="RPM"
                inputType="number"
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-bold">NCR of main engine</label>
              <InputCell
                disabled={readonly}
                name="mainEngineNcrBhp"
                value={form.mainEngineNcrBhp}
                onChange={handleChange}
                trailingText="bhp"
                inputType="number"
              />
            </div>

            <div>
              <label className="block text-sm font-bold">NCR of main engine</label>
              <InputCell
                disabled={readonly}
                name="mainEngineNcrRpm"
                value={form.mainEngineNcrRpm}
                onChange={handleChange}
                trailingText="RPM"
                inputType="number"
              />
            </div>
          </div>

          {/* Fuel */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Fuel</h2>

          <div className="grid grid-cols-4 gap-4">
            {/* Main propulsion fuel type */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">What type of fuel is used for main propulsion?</label>
              <div className="flex">
                <select
                  disabled={readonly}
                  name="mainPropulsionFuelType"
                  value={form.mainPropulsionOtherFuelSelected ? "Other" : form.mainPropulsionFuelType || ""}
                  onChange={(e) => {
                    const selected = e.target.value;
                    if (selected === "Other") {
                      setForm((prev) => ({
                        ...prev,
                        mainPropulsionFuelType: "",
                        mainPropulsionOtherFuelSelected: true,
                      }));
                    } else {
                      setForm((prev) => ({
                        ...prev,
                        mainPropulsionFuelType: selected,
                        mainPropulsionOtherFuelSelected: false,
                      }));
                    }
                  }}
                  className={`mt-1 ${form.mainPropulsionOtherFuelSelected ? "w-1/2" : "w-full"} rounded-md border p-2 focus:outline-none focus:ring`}
                >
                  <option value=""></option>
                  <option value="VLSFO">VLSFO</option>
                  <option value="HFO">HFO</option>
                  <option value="LSG / MGO">LSG / MGO</option>
                  <option value="LNG">LNG</option>
                  <option value="Methane">Methane</option>
                  <option value="Other">Other</option>
                </select>

                {form.mainPropulsionOtherFuelSelected && (
                  <input
                    type="text"
                    name="mainPropulsionFuelType"
                    value={form.mainPropulsionFuelType}
                    onChange={handleChange}
                    placeholder="Specify other fuel type"
                    className="ml-5 mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                    disabled={readonly}
                  />
                )}
              </div>
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("mainPropulsionFuelTypeComments")}
                >
                  Comments
                </button>
              </label>
              {(form.mainPropulsionFuelTypeComments || isCommentsVisible("mainPropulsionFuelTypeComments")) && (
                <textarea
                  disabled={readonly}
                  name="mainPropulsionFuelTypeComments"
                  value={form.mainPropulsionFuelTypeComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            {/* Use calculated total? */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">Use calculated total for fuel oil tank capacity</label>
              <p className="text-xs font-medium">if no, then enter manual total below).: Excluding umpumpables</p>
              <div className="mt-1 flex space-x-2">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="mainPropulsionFuelUseCalculated"
                    value="yes"
                    checked={form.mainPropulsionFuelUseCalculated === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="mainPropulsionFuelUseCalculated"
                    value="no"
                    checked={form.mainPropulsionFuelUseCalculated === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
              </div>
            </div>

            {/* Calculated total capacity */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                Calculated total for fuel oil tank capacity: Excluding umpumpables
              </label>
              <input
                disabled={readonly}
                type="text"
                name="mainPropulsionFuelCalculatedTotal"
                value={form.mainPropulsionFuelCalculatedTotal}
                onChange={handleChange}
                placeholder="Excl unpumpables"
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">Total Fuel Oil (Capacity)</label>
              <InputCell
                disabled={readonly}
                name="mainPropulsionFuelTotal"
                value={form.mainPropulsionFuelTotal}
                onChange={handleChange}
                trailingText="Cu. Metres"
                inputType="number"
              />
            </div>
          </div>

          {/* Generating plant fuel type */}
          <div className="mt-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">What type of fuel is used in the generating plant?:</label>
              <input
                disabled={readonly}
                type="text"
                name="generatingPlantFuelType"
                value={form.generatingPlantFuelType}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("generatingPlantFuelTypeComments")}
                >
                  Comments
                </button>
              </label>
              {(form.generatingPlantFuelTypeComments || isCommentsVisible("generatingPlantFuelTypeComments")) && (
                <textarea
                  disabled={readonly}
                  name="generatingPlantFuelTypeComments"
                  value={form.generatingPlantFuelTypeComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            {/* Use calculated total? */}
            <div className="col-span-1">
              <label className="block text-sm font-bold">Use calculated total for diesel oil tank capacity</label>
              <p className="text-xs font-medium">(if no, then enter manual total below).: Excluding umpumpables</p>
              <div className="mt-1 flex space-x-2">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="generatingPlantFuelUseCalculated"
                    value="yes"
                    checked={form.generatingPlantFuelUseCalculated === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="generatingPlantFuelUseCalculated"
                    value="no"
                    checked={form.generatingPlantFuelUseCalculated === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
              </div>
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">Calculated total for diesel oil tank capacity:</label>
              <input
                disabled={readonly}
                type="text"
                name="generatingPlantFuelCalculatedTotal"
                value={form.generatingPlantFuelCalculatedTotal}
                onChange={handleChange}
                placeholder="Excl unpumpables"
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-bold">Total Diesel Oil (Capacity)</label>
              <InputCell
                disabled={readonly}
                name="generatingPlantFuelTotal"
                value={form.generatingPlantFuelTotal}
                onChange={handleChange}
                trailingText="Cu. Metres"
                inputType="number"
              />
            </div>
          </div>

          {/* Speed */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Speed</h2>

          <label className="block text-xs focus:outline-none">
            (Knots - Weather, Sea, No current, Propeller slip references, etc.)
          </label>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-bold">Max speed in ballast condition</label>
              <InputCell
                disabled={readonly}
                name="ballastMaxSpeedKnots"
                value={form.ballastMaxSpeedKnots}
                onChange={handleChange}
                trailingText="Knots(WSNP)"
                inputType="number"
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("ballastMaxSpeedKnotsComments")}
                >
                  Comments
                </button>
              </label>
              {(form.ballastMaxSpeedKnotsComments || isCommentsVisible("ballastMaxSpeedKnotsComments")) && (
                <textarea
                  disabled={readonly}
                  name="ballastMaxSpeedKnotsComments"
                  value={form.ballastMaxSpeedKnotsComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-bold">Economic speed in ballast condition</label>
              <InputCell
                disabled={readonly}
                name="ballastEconSpeedKnots"
                value={form.ballastEconSpeedKnots}
                onChange={handleChange}
                trailingText="Knots(WSNP)"
                inputType="number"
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("ballastEconSpeedKnotsComments")}
                >
                  Comments
                </button>
              </label>
              {(form.ballastEconSpeedKnotsComments || isCommentsVisible("ballastEconSpeedKnotsComments")) && (
                <textarea
                  disabled={readonly}
                  name="ballastEconSpeedKnotsComments"
                  value={form.ballastEconSpeedKnotsComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-bold">Max speed in fully laden condition</label>
              <InputCell
                disabled={readonly}
                name="ladenMaxSpeedKnots"
                value={form.ladenMaxSpeedKnots}
                onChange={handleChange}
                trailingText="Knots(WSNP)"
                inputType="number"
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("ladenMaxSpeedKnotsComments")}
                >
                  Comments
                </button>
              </label>
              {(form.ladenMaxSpeedKnotsComments || isCommentsVisible("ladenMaxSpeedKnotsComments")) && (
                <textarea
                  disabled={readonly}
                  name="ladenMaxSpeedKnotsComments"
                  value={form.ladenMaxSpeedKnotsComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-bold">Economic speed in fully laden condition</label>
              <InputCell
                disabled={readonly}
                name="ballastMaxSpeedKnots"
                value={form.ballastMaxSpeedKnots}
                onChange={handleChange}
                trailingText="Knots(WSNP)"
                inputType="number"
              />
              <label className="block text-sm font-bold">
                <button
                  className={theme.balticQuestionnaire.blueText}
                  onClick={() => toggleComments("ladenEconSpeedKnotsComments")}
                >
                  Comments
                </button>
              </label>
              {(form.ladenEconSpeedKnotsComments || isCommentsVisible("ladenEconSpeedKnotsComments")) && (
                <textarea
                  disabled={readonly}
                  name="ladenEconSpeedKnotsComments"
                  value={form.ladenEconSpeedKnotsComments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              )}
            </div>
          </div>

          {/* Consumptions & */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Consumptions</h2>
          <div className="flex">
            <div className={twMerge("flex w-[32px] items-center justify-center", theme.balticQuestionnaire.grayBg)}>
              <div
                className={twMerge(
                  "-rotate-90 items-center justify-center rounded-sm px-2 py-1",
                  theme.balticQuestionnaire.grayBg,
                )}
              >
                <span className="block origin-top-left transform whitespace-nowrap text-lg font-semibold leading-none">
                  Passage
                </span>
              </div>
            </div>
            <div className="ml-2 flex-1">
              <div className="mb-4 grid grid-cols-4 gap-4">
                <div className="col-span-1">
                  <label className="block text-xs font-semibold">Main engine ballast consumption</label>
                  <InputCell
                    disabled={readonly}
                    name="mainEngineBallastConsMT"
                    value={form.mainEngineBallastConsMT}
                    onChange={handleChange}
                    trailingText="MT/Day"
                    inputType="number"
                  />
                  <label className="block text-sm font-bold">
                    <button
                      className={theme.balticQuestionnaire.blueText}
                      onClick={() => toggleComments("mainEngineBallastConsMTComments")}
                    >
                      Comments
                    </button>
                  </label>
                  {(form.mainEngineBallastConsMTComments || isCommentsVisible("mainEngineBallastConsMTComments")) && (
                    <textarea
                      disabled={readonly}
                      name="mainEngineBallastConsMTComments"
                      value={form.mainEngineBallastConsMTComments}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                    />
                  )}
                </div>

                <div className="col-span-1">
                  <label className="block text-xs font-semibold">Aux engine ballast consumption</label>
                  <InputCell
                    disabled={readonly}
                    name="auxEngineBallastConsMT"
                    value={form.auxEngineBallastConsMT}
                    onChange={handleChange}
                    trailingText="MT/Day"
                    inputType="number"
                  />
                  <label className="block text-sm font-bold">
                    <button
                      className={theme.balticQuestionnaire.blueText}
                      onClick={() => toggleComments("auxEngineBallastConsMTComments")}
                    >
                      Comments
                    </button>
                  </label>
                  {(form.auxEngineBallastConsMTComments || isCommentsVisible("auxEngineBallastConsMTComments")) && (
                    <textarea
                      disabled={readonly}
                      name="auxEngineBallastConsMTComments"
                      value={form.auxEngineBallastConsMTComments}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                    />
                  )}
                </div>
              </div>

              <div className="mb-4 grid grid-cols-4 gap-4">
                <div className="col-span-1">
                  <label className="block text-xs font-semibold">Main engine laden consumption</label>
                  <InputCell
                    disabled={readonly}
                    name="mainEngineLadenConsMT"
                    value={form.mainEngineLadenConsMT}
                    onChange={handleChange}
                    trailingText="MT/Day"
                    inputType="number"
                  />
                  <label className="block text-sm font-bold">
                    <button
                      className={theme.balticQuestionnaire.blueText}
                      onClick={() => toggleComments("mainEngineLadenConsMTComments")}
                    >
                      Comments
                    </button>
                  </label>
                  {(form.mainEngineLadenConsMTComments || isCommentsVisible("mainEngineLadenConsMTComments")) && (
                    <textarea
                      disabled={readonly}
                      name="mainEngineLadenConsMTComments"
                      value={form.mainEngineLadenConsMTComments}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                    />
                  )}
                </div>

                <div className="col-span-1">
                  <label className="block text-xs font-semibold">Aux engine laden consumption</label>
                  <InputCell
                    disabled={readonly}
                    name="auxEngineLadenConsMT"
                    value={form.auxEngineLadenConsMT}
                    onChange={handleChange}
                    trailingText="MT/Day"
                    inputType="number"
                  />
                  <label className="block text-sm font-bold">
                    <button
                      className={theme.balticQuestionnaire.blueText}
                      onClick={() => toggleComments("auxEngineLadenConsMTComments")}
                    >
                      Comments
                    </button>
                  </label>
                  {(form.auxEngineLadenConsMTComments || isCommentsVisible("auxEngineLadenConsMTComments")) && (
                    <textarea
                      disabled={readonly}
                      name="auxEngineLadenConsMTComments"
                      value={form.auxEngineLadenConsMTComments}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={twMerge("my-4 h-px w-[calc(100%-49%)]", theme.balticQuestionnaire.dividerBg)} />
          <div className="flex">
            <div className={twMerge("flex w-[32px] items-center justify-center", theme.balticQuestionnaire.grayBg)}>
              <div
                className={twMerge(
                  "-rotate-90 items-center justify-center rounded-sm px-2 py-1",
                  theme.balticQuestionnaire.grayBg,
                )}
              >
                <span className="block origin-top-left transform whitespace-nowrap text-lg font-semibold leading-none">
                  In Port
                </span>
              </div>
            </div>
            <div className="ml-2 flex-1">
              <div className="mb-4 grid grid-cols-4 gap-4">
                <div className="col-span-1">
                  <label className="block text-xs font-semibold">Main engine working consumption</label>
                  <InputCell
                    disabled={readonly}
                    name="mainEngineWorkingConsMT"
                    value={form.mainEngineWorkingConsMT}
                    onChange={handleChange}
                    trailingText="MT/Day"
                    inputType="number"
                  />
                  <label className="block text-sm font-bold">
                    <button
                      className={theme.balticQuestionnaire.blueText}
                      onClick={() => toggleComments("mainEngineWorkingConsMTComments")}
                    >
                      Comments
                    </button>
                  </label>
                  {(form.mainEngineWorkingConsMTComments || isCommentsVisible("mainEngineWorkingConsMTComments")) && (
                    <textarea
                      disabled={readonly}
                      name="mainEngineWorkingConsMTComments"
                      value={form.mainEngineWorkingConsMTComments}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                    />
                  )}
                </div>

                <div className="col-span-1">
                  <label className="block text-xs font-semibold">Aux engine(s) working consumption</label>
                  <InputCell
                    disabled={readonly}
                    name="auxEngineWorkingConsMT"
                    value={form.auxEngineWorkingConsMT}
                    onChange={handleChange}
                    trailingText="MT/Day"
                    inputType="number"
                  />
                  <label className="block text-sm font-bold">
                    <button
                      className={theme.balticQuestionnaire.blueText}
                      onClick={() => toggleComments("auxEngineWorkingConsMTComments")}
                    >
                      Comments
                    </button>
                  </label>
                  {(form.auxEngineWorkingConsMTComments || isCommentsVisible("auxEngineWorkingConsMTComments")) && (
                    <textarea
                      disabled={readonly}
                      name="auxEngineWorkingConsMTComments"
                      value={form.auxEngineWorkingConsMTComments}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                    />
                  )}
                </div>
              </div>
              <div className="mb-4 grid grid-cols-4 gap-4">
                <div className="col-span-1">
                  <label className="block text-xs font-semibold">Main engine consumption at anchor/idle</label>
                  <InputCell
                    disabled={readonly}
                    name="mainEngineAnchorConsMT"
                    value={form.mainEngineAnchorConsMT}
                    onChange={handleChange}
                    trailingText="MT/Day"
                    inputType="number"
                  />
                  <label className="block text-sm font-bold">
                    <button
                      className={theme.balticQuestionnaire.blueText}
                      onClick={() => toggleComments("mainEngineAnchorConsMTComments")}
                    >
                      Comments
                    </button>
                  </label>
                  {(form.mainEngineAnchorConsMTComments || isCommentsVisible("mainEngineAnchorConsMTComments")) && (
                    <textarea
                      disabled={readonly}
                      name="mainEngineAnchorConsMTComments"
                      value={form.mainEngineAnchorConsMTComments}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                    />
                  )}
                </div>

                <div className="col-span-1">
                  <label className="block text-xs font-semibold">Aux engine consumption at anchor/idle</label>
                  <InputCell
                    disabled={readonly}
                    name="auxEngineAnchorConsMT"
                    value={form.auxEngineAnchorConsMT}
                    onChange={handleChange}
                    trailingText="MT/Day"
                    inputType="number"
                  />
                  <label className="block text-sm font-bold">
                    <button
                      className={theme.balticQuestionnaire.blueText}
                      onClick={() => toggleComments("auxEngineAnchorConsMTComments")}
                    >
                      Comments
                    </button>
                  </label>
                  {(form.auxEngineAnchorConsMTComments || isCommentsVisible("auxEngineAnchorConsMTComments")) && (
                    <textarea
                      disabled={readonly}
                      name="auxEngineAnchorConsMTComments"
                      value={form.auxEngineAnchorConsMTComments}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                    />
                  )}
                </div>
              </div>
              <div className="mb-4 grid grid-cols-4 gap-4">
                <div className="col-span-1">
                  <label className="block text-xs font-semibold">Main engine consumption (other)</label>
                  <InputCell
                    disabled={readonly}
                    name="mainEngineOtherConsMT"
                    value={form.mainEngineOtherConsMT}
                    onChange={handleChange}
                    trailingText="MT/Day"
                    inputType="number"
                  />
                  <label className="block text-sm font-bold">
                    <button
                      className={theme.balticQuestionnaire.blueText}
                      onClick={() => toggleComments("mainEngineOtherConsMTComments")}
                    >
                      Comments
                    </button>
                  </label>
                  {(form.mainEngineOtherConsMTComments || isCommentsVisible("mainEngineOtherConsMTComments")) && (
                    <textarea
                      disabled={readonly}
                      name="mainEngineOtherConsMTComments"
                      value={form.mainEngineOtherConsMTComments}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                    />
                  )}
                </div>

                <div className="col-span-1">
                  <label className="block text-xs font-semibold">Aux engine consumption (other)</label>
                  <InputCell
                    disabled={readonly}
                    name="auxEngineOtherConsMT"
                    value={form.auxEngineOtherConsMT}
                    onChange={handleChange}
                    trailingText="MT/Day"
                    inputType="number"
                  />
                  <label className="block text-sm font-bold">
                    <button
                      className={theme.balticQuestionnaire.blueText}
                      onClick={() => toggleComments("auxEngineOtherConsMTComments")}
                    >
                      Comments
                    </button>
                  </label>
                  {(form.auxEngineOtherConsMTComments || isCommentsVisible("auxEngineOtherConsMTComments")) && (
                    <textarea
                      disabled={readonly}
                      name="auxEngineOtherConsMTComments"
                      value={form.auxEngineOtherConsMTComments}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Additional Information - Speed and Consumptions</label>
              <textarea
                disabled={readonly}
                name="additionalInfoSpeedConsumption"
                value={form.additionalInfoSpeedConsumption}
                onChange={handleChange}
                rows={3}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          {/* Environmental/Emissions */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Environmental / Emissions</h2>

          {/* EEDI */}
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Does the vessel have an EEDI Rating number?</label>

              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="hasEEDIRating"
                    value="yes"
                    checked={form.hasEEDIRating === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="hasEEDIRating"
                    value="no"
                    checked={form.hasEEDIRating === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
              </div>
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">Energy Efficiency Design Index (EEDI) rating number:</label>
              <input
                disabled={readonly}
                type="text"
                name="eediNumber"
                value={form.eediNumber}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">
                If No then provide reason vessel does not have an Energy Efficiency Design Index (EEDI):
              </label>
              <input
                disabled={readonly}
                type="text"
                name="eediNoReason"
                value={form.eediNoReason}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">
                Is the EEDI rating verified by Class, 3rd Party or Owner?:
              </label>
              <select
                disabled={readonly}
                name="eediVerifiedBy"
                value={form.eediVerifiedBy}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              >
                <option value=""></option>
                <option value="Class">Class</option>
                <option value="3rd Party">3rd Party</option>
                <option value="Owner">Owner</option>
              </select>
            </div>
          </div>

          {/* EEXI */}
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="mt-4 block text-sm font-bold">Does the vessel have an EEXI Rating number?:</label>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="hasEEXIRating"
                    value="yes"
                    checked={form.hasEEXIRating === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="hasEEXIRating"
                    value="no"
                    checked={form.hasEEXIRating === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
              </div>
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold"> If yes then provide EEXI rating:</label>
              <input
                disabled={readonly}
                type="number"
                name="eexiRating"
                value={form.eexiRating}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">
                If No then provide reason the vessel does not have an EEXI Rating number:
              </label>
              <input
                disabled={readonly}
                type="text"
                name="eexiNoReason"
                value={form.eexiNoReason}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">
                Is the EEXI rating verified by Class, 3rd Party or Owner?:
              </label>
              <select
                disabled={readonly}
                name="eexiVerifiedBy"
                value={form.eexiVerifiedBy}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              >
                <option value=""></option>
                <option value="Class">Class</option>
                <option value="3rd Party">3rd Party</option>
                <option value="Owner">Owner</option>
              </select>
            </div>
          </div>

          {/* CII */}
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="mt-4 block text-sm font-bold">Does the vessel have a CII rating?</label>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="hasCIIRating"
                    value="yes"
                    checked={form.hasCIIRating === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="hasCIIRating"
                    value="no"
                    checked={form.hasCIIRating === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
              </div>
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">If yes then provide CII rating:</label>
              <select
                disabled={readonly}
                name="ciiRating"
                value={form.ciiRating}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              >
                <option value=""></option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
                <option value="E">E</option>
              </select>
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">
                If No then provide reason vessel does not have a CII Rating number:
              </label>
              <input
                disabled={readonly}
                type="text"
                name="ciiNoReason"
                value={form.ciiNoReason}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">
                Is the CII rating verified by Class, 3rd Party or Owner?:
              </label>
              <select
                disabled={readonly}
                name="ciiVerifiedBy"
                value={form.ciiVerifiedBy}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              >
                <option value=""></option>
                <option value="Class">Class</option>
                <option value="3rd Party">3rd Party</option>
                <option value="Owner">Owner</option>
              </select>
            </div>

            <div className="col-span-1"></div>
            <div className="col-span-1">
              <label className="block text-xs font-bold">Year To Date CII</label>
              <Datepicker
                disabled={readonly}
                selected={formDate(form.yearToDateCII ?? "")}
                onChange={(date) => updateDate(date, "yearToDateCII")}
              />
            </div>
          </div>

          {/* EIV */}
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="mt-4 block text-sm font-bold">Does the vessel have an EIV Rating number?:</label>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="hasEIVRating"
                    value="yes"
                    checked={form.hasEIVRating === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="hasEIVRating"
                    value="no"
                    checked={form.hasEIVRating === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
              </div>
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">If yes then provide EIV rating:</label>
              <input
                disabled={readonly}
                type="number"
                name="eivRating"
                value={form.eivRating}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>

            <div>
              <label className="block text-xs font-bold">
                If No then provide reason why vessel does not have an EIV Rating number:
              </label>
              <input
                disabled={readonly}
                type="text"
                name="eivNoReason"
                value={form.eivNoReason}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-xs font-bold">
                Is the EIV rating verified by Class, 3rd Party or Owner?
              </label>
              <select
                disabled={readonly}
                name="eivVerifiedBy"
                value={form.eivVerifiedBy}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border p-2 text-sm focus:outline-none focus:ring"
              >
                <option value=""></option>
                <option value="Class">Class</option>
                <option value="3rd Party">3rd Party</option>
                <option value="Owner">Owner</option>
              </select>
            </div>
          </div>

          {/* NOx */}
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                What is the ships NOx control level (Tier I, Tier II, and Tier III)?:
              </label>
              <select
                disabled={readonly}
                name="shipNOxControlLevel"
                value={form.shipNOxControlLevel}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              >
                <option value=""></option>
                <option value="Tier I">Tier I</option>
                <option value="Tier II">Tier II</option>
                <option value="Tier III">Tier III</option>
              </select>
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="mt-2 block text-sm font-bold">
                List of equipment fitted for NOx Tier III achievement for all engines (LP Selective catalytic reduction,
                HP Selective catalytic reduction, Exhaust gas recirculation, Alternative fuel etc...):
              </label>
              <div>
                <label className="inline-flex items-center space-x-1">
                  <input
                    disabled={readonly}
                    type="checkbox"
                    name="noxReductionTech"
                    value="LP SCR"
                    checked={isChecked("LP SCR")}
                    onChange={handleChange}
                    className="form-checkbox text-green"
                  />
                  <span className="text-sm">LP Selective catalytic reduction</span>
                </label>
              </div>
              <div>
                <label className="inline-flex items-center space-x-1">
                  <input
                    disabled={readonly}
                    type="checkbox"
                    name="noxReductionTech"
                    value="HP SCR"
                    checked={isChecked("HP SCR")}
                    onChange={handleChange}
                    className="form-checkbox text-green"
                  />
                  <span className="text-sm">HP Selective catalytic reduction</span>
                </label>
              </div>
              <div>
                <label className="inline-flex items-center space-x-1">
                  <input
                    disabled={readonly}
                    type="checkbox"
                    name="noxReductionTech"
                    value="EGR"
                    checked={isChecked("EGR")}
                    onChange={handleChange}
                    className="form-checkbox text-green"
                  />
                  <span className="text-sm">Exhaust gas recirculation</span>
                </label>
              </div>
              <div>
                <label className="inline-flex items-center space-x-1">
                  <input
                    disabled={readonly}
                    type="checkbox"
                    name="noxReductionTech"
                    value="Alternative fuel"
                    checked={isChecked("Alternative fuel")}
                    onChange={handleChange}
                    className="form-checkbox text-green"
                  />
                  <span className="text-sm">Alternative fuel</span>
                </label>
              </div>
              <div>
                <label className="inline-flex items-center space-x-1">
                  <input
                    disabled={readonly}
                    type="checkbox"
                    name="noxReductionTech"
                    value="Other"
                    checked={isChecked("Other")}
                    onChange={handleChange}
                    className="form-checkbox text-green"
                  />
                  <span className="text-sm">Other (specify)</span>
                </label>
              </div>
            </div>
          </div>

          {/* Biofuel */}
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Owners accept Biofuel blends ISO 8217/2024</label>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="ownersAcceptBiofuel"
                    value="yes"
                    checked={form.ownersAcceptBiofuel === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="ownersAcceptBiofuel"
                    value="no"
                    checked={form.ownersAcceptBiofuel === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="ownersAcceptBiofuel"
                    value="n/a"
                    checked={form.ownersAcceptBiofuel === "n/a"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">N/A</span>
                </label>
              </div>
            </div>
          </div>

          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Exhaust Gas Cleaning System/Scrubber</h2>

          {/* Exhaust Gas Cleaning */}
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Does the vessel use an Exhaust Gas Cleaning System?</label>
              <div className="mt-1 flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselHasEGCS"
                    value="yes"
                    checked={form.vesselHasEGCS === "yes"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    disabled={readonly}
                    type="radio"
                    name="vesselHasEGCS"
                    value="no"
                    checked={form.vesselHasEGCS === "no"}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-1">No</span>
                </label>
              </div>
            </div>
          </div>

          {/* Scrubber type */}
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">
                What is the type of scrubber fitted as part of the EGCS onboard?
              </label>
              <select
                disabled={readonly}
                name="scrubberType"
                value={form.scrubberType}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              >
                <option value=""></option>
                <option value="Open Loop">Open Loop</option>
                <option value="Closed Loop">Closed Loop</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>
          </div>

          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-bold">Scrubber holding tank capacity when in closed loop:</label>
              <input
                disabled={readonly}
                type="text"
                name="scrubberTankCapacity"
                value={form.scrubberTankCapacity}
                onChange={handleChange}
                className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          </div>

          <br />
        </form>
      </div>
    </LoadingWrapper>
  );
};
